import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import { Badge } from '../components/ui/badge';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, Eye, Pause, Play, Search,
  TrendingUp, MessageSquare, Hash, AlertCircle, Filter, RefreshCw, FileText,
  Activity, Zap, Users, BarChart3, ThumbsUp, ThumbsDown, Minus, ExternalLink, Target
} from 'lucide-react';
import { useBrandLabel, useIsDemoBrand } from '../lib/demoBrand';
import { useDemoProfile } from '../hooks/useDemoProfile';

const API = process.env.REACT_APP_BACKEND_URL;

// Mock data for the demo since content_items collection may be empty
const MOCK_CONTENT = [
  { id: 'c1', title: 'Summer hydration routine with Lumina', creator_name: 'Mia Chen', creator_handle: '@sleepcoach_mia', platform: 'Instagram', caption: 'Loving my new @LuminaWellness Electrolyte Mix routine! Perfect for hot days. #ad #LuminaHydration #SummerVibes', safety_status: 'safe', sentiment: 'positive', compliance_score: 100, hashtags: ['#ad', '#LuminaHydration', '#SummerVibes'], issues: [], engagement: '12.4K', reach: '89K', wave: 'Summer Hydration Wave', posted: '2 hours ago' },
  { id: 'c2', title: 'Morning energy boost', creator_name: 'Jax Rivera', creator_handle: '@hydratewithjax', platform: 'TikTok', caption: 'This @LuminaWellness Energy Blend hits different. Best pre-workout I have tried! #ad #LuminaHydration', safety_status: 'safe', sentiment: 'positive', compliance_score: 100, hashtags: ['#ad', '#LuminaHydration'], issues: [], engagement: '8.7K', reach: '124K', wave: 'Summer Hydration Wave', posted: '3 hours ago' },
  { id: 'c3', title: 'Honest product review', creator_name: 'Lena Park', creator_handle: '@fitnessmom_lena', platform: 'YouTube', caption: 'Real talk about @LuminaWellness Sleep Recovery — does it actually work? Full review inside.', safety_status: 'warning', sentiment: 'neutral', compliance_score: 70, hashtags: [], issues: [{ type: 'missing_disclosure', message: 'No #ad or sponsorship disclosure found', severity: 'medium' }], engagement: '3.2K', reach: '42K', wave: 'Sleep Recovery Wave', posted: '5 hours ago' },
  { id: 'c4', title: 'Competitor mention detected', creator_name: 'Devon Brooks', creator_handle: '@devon.hydrates', platform: 'TikTok', caption: 'Switching from Gatorade to @LuminaWellness — taste comparison! The worst thing about Gatorade is the sugar. #ad #LuminaHydration', safety_status: 'warning', sentiment: 'neutral', compliance_score: 100, hashtags: ['#ad', '#LuminaHydration'], issues: [{ type: 'competitor_mention', message: 'Competitor brand "Gatorade" mentioned', severity: 'medium' }], engagement: '15.8K', reach: '201K', wave: 'Summer Hydration Wave', posted: '6 hours ago' },
  { id: 'c5', title: 'Negative sentiment post', creator_name: 'Kai Williams', creator_handle: '@kai.recovers', platform: 'Instagram', caption: 'Terrible taste honestly. @LuminaWellness Recovery Powder was disappointing and the worst supplement I tried this year. Bad experience. #ad', safety_status: 'flagged', sentiment: 'negative', compliance_score: 100, hashtags: ['#ad'], issues: [{ type: 'negative_sentiment', severity: 'high', message: 'Strong negative sentiment detected: "terrible", "worst", "bad"' }], engagement: '1.1K', reach: '18K', wave: 'Sleep Recovery Wave', posted: '8 hours ago' },
  { id: 'c6', title: 'Missing disclosure + off-brand', creator_name: 'Marcus Johnson', creator_handle: '@marcusjfit', platform: 'TikTok', caption: 'LuminaWellness gave me this to try. Mixed feelings — the flavor is ok but kinda overpriced for what you get.', safety_status: 'flagged', sentiment: 'negative', compliance_score: 0, hashtags: [], issues: [{ type: 'missing_disclosure', message: 'No #ad or sponsorship disclosure', severity: 'high' }, { type: 'negative_sentiment', severity: 'medium', message: 'Negative tone detected' }], engagement: '5.4K', reach: '67K', wave: 'Morning Ritual Wave', posted: '10 hours ago' },
  { id: 'c7', title: 'Gym routine integration', creator_name: 'Andre Mitchell', creator_handle: '@andrefuels', platform: 'Instagram', caption: 'Pre-workout just leveled up with @LuminaWellness Energy Blend! Clean ingredients, smooth energy. #ad #LuminaHydration #GymLife', safety_status: 'safe', sentiment: 'positive', compliance_score: 100, hashtags: ['#ad', '#LuminaHydration', '#GymLife'], issues: [], engagement: '6.3K', reach: '71K', wave: 'Summer Hydration Wave', posted: '12 hours ago' },
  { id: 'c8', title: 'Yoga and hydration', creator_name: 'Priya Sharma', creator_handle: '@priya.mindful', platform: 'Instagram', caption: 'My mindful hydration ritual starts with @LuminaWellness Tropical Electrolyte. Namaste. #ad #LuminaHydration #YogaLife', safety_status: 'safe', sentiment: 'positive', compliance_score: 100, hashtags: ['#ad', '#LuminaHydration', '#YogaLife'], issues: [], engagement: '4.8K', reach: '52K', wave: 'Morning Ritual Wave', posted: '14 hours ago' },
  { id: 'c9', title: 'Smoothie recipe', creator_name: 'Luna Ramirez', creator_handle: '@lunasips', platform: 'TikTok', caption: 'Berry blast smoothie with @LuminaWellness Electrolyte Mix = chef kiss. Recipe in bio! #ad #LuminaHydration #SmoothieQueen', safety_status: 'safe', sentiment: 'positive', compliance_score: 100, hashtags: ['#ad', '#LuminaHydration', '#SmoothieQueen'], issues: [], engagement: '22.1K', reach: '312K', wave: 'Summer Hydration Wave', posted: '1 day ago' },
  { id: 'c10', title: 'Sleep routine ASMR', creator_name: 'Sophia Wright', creator_handle: '@sophianaps', platform: 'TikTok', caption: 'ASMR sleep routine with @LuminaWellness Sleep Recovery Blend. This knocks me out in 20 min. #ad #LuminaHydration #SleepASMR', safety_status: 'safe', sentiment: 'positive', compliance_score: 100, hashtags: ['#ad', '#LuminaHydration', '#SleepASMR'], issues: [], engagement: '18.9K', reach: '245K', wave: 'Sleep Recovery Wave', posted: '1 day ago' },
];

const STATUS_COLORS = {
  safe: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20', icon: CheckCircle },
  warning: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', icon: AlertTriangle },
  flagged: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', icon: XCircle },
  paused: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: Pause },
};

const SENTIMENT_ICONS = {
  positive: { icon: ThumbsUp, color: 'text-green-400' },
  neutral: { icon: Minus, color: 'text-gray-400' },
  negative: { icon: ThumbsDown, color: 'text-red-400' },
};

const ACTION_TO_STATUS = {
  approve: 'safe',
  pause: 'paused',
  revision: 'warning',
};

const STATUS_BORDER = {
  flagged: 'border-red-500/20',
  warning: 'border-yellow-500/20',
};

function getScoreColor(score) {
  if (score >= 80) return '#4ade80';
  if (score >= 60) return '#F59E0B';
  return '#EF4444';
}

function getScoreLabel(score) {
  if (score >= 80) return 'Healthy';
  if (score >= 60) return 'Attention';
  return 'At Risk';
}

/**
 * Transform Lumina-themed mock content into a brand-specific equivalent for
 * the seeded demo presenter brands. The fictional flagged/safe content is
 * the same set of moderation patterns; we just relabel to match the brand
 * the presenter is showcasing so investors don't see a wellness brand's
 * captions when they're inside Helix Motors or Vector Pictures.
 */
function relabelForBrand(items, profile) {
  if (!profile?.is_demo || !profile.brand_name) return items;
  const brand = profile.brand_name;
  const handle = '@' + brand.replace(/[\s.&]/g, '').slice(0, 28);
  const wave = (profile.skus?.[0]?.calendar?.[0]?.wave) || `${brand} Wave`;
  return items.map(item => ({
    ...item,
    title: item.title
      .replaceAll('Lumina', brand)
      .replaceAll('hydration routine', wave.split('·')[0].trim().toLowerCase()),
    caption: item.caption
      .replaceAll('@LuminaWellness', handle)
      .replaceAll('Lumina', brand)
      .replaceAll('LuminaHydration', brand.replace(/\s/g, '')),
    hashtags: (item.hashtags || []).map(h => h.replaceAll('LuminaHydration', brand.replace(/\s/g, ''))),
    wave: wave.length > 50 ? `${brand} Wave` : wave,
  }));
}

export default function BrandSafetyPage() {
  const brandLabel = useBrandLabel();
  const isDemoBrand = useIsDemoBrand();
  const { profile } = useDemoProfile();
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Demo brands keep the rich mock content for investor pitches but
  // relabel the brand mentions to match the brand they just logged into.
  const [contentItems, setContentItems] = useState(
    isDemoBrand ? relabelForBrand(MOCK_CONTENT, profile) : []
  );
  const [loading, setLoading] = useState(false);

  // Re-relabel when profile loads (it arrives async after first paint).
  useEffect(() => {
    if (!isDemoBrand) return;
    setContentItems(relabelForBrand(MOCK_CONTENT, profile));
  }, [profile, isDemoBrand]);

  // Try to load real data from backend, fall back to mock
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${API}/api/brand/safety-dashboard`, { withCredentials: true });
        if (data.content && data.content.length > 0) {
          setContentItems(data.content);
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.warn('Using mock safety data:', err.message);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo(() => {
    const safe = contentItems.filter(c => c.safety_status === 'safe').length;
    const warning = contentItems.filter(c => c.safety_status === 'warning').length;
    const flagged = contentItems.filter(c => c.safety_status === 'flagged').length;
    const paused = contentItems.filter(c => c.safety_status === 'paused').length;
    const total = contentItems.length;
    return {
      total, safe, warning, flagged, paused,
      overallScore: total > 0 ? Math.round((safe / total) * 100) : 100,
      complianceRate: total > 0 ? Math.round((contentItems.filter(c => c.compliance_score === 100).length / total) * 100) : 100,
    };
  }, [contentItems]);

  const filtered = useMemo(() => {
    let items = [...contentItems];
    if (filter !== 'all') items = items.filter(c => c.safety_status === filter);
    if (searchQuery) items = items.filter(c =>
      (c.creator_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.caption || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    return items;
  }, [contentItems, filter, searchQuery]);

  const handleAction = (id, action) => {
    const nextStatus = ACTION_TO_STATUS[action];
    if (!nextStatus) return;
    setContentItems(prev => prev.map(c => (c.id === id ? { ...c, safety_status: nextStatus } : c)));
  };

  const safetyScoreColor = getScoreColor(summary.overallScore);
  const safetyScoreLabel = getScoreLabel(summary.overallScore);
  const scoreDash = (summary.overallScore / 100) * 251.3;

  return (
    <DashboardLayout>
      <div className="p-5 lg:p-8 space-y-5 max-w-[1500px] mx-auto" data-testid="brand-safety-page">
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-2">
            <Shield className="h-6 w-6 text-[#00D4FF]" />Brand Safety Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">{brandLabel} &bull; Real-time content monitoring, FTC compliance & sentiment tracking</p>
        </div>
        <span className="text-[8px] text-gray-600 uppercase tracking-wider absolute top-5 right-8">Patent Pending</span>

        {/* Pre-Wave Safety Predictor */}
        <div className="bg-[#242730] border border-[#2E3240] rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="h-5 w-5 text-[#00D4FF]" />
            <div>
              <p className="text-xs font-bold text-white">Pre-Wave Safety Score Predictor</p>
              <p className="text-[10px] text-gray-500">Predicted safety score for your next wave based on creator history and content patterns</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-black text-[#4ade80]">96.2%</span>
            <span className="text-[9px] text-green-400 px-2 py-1 rounded-full bg-green-500/10 border border-green-500/20 font-semibold">Low Risk</span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="safety-summary">
          {/* Safety Score Gauge */}
          <div className="bg-[#242730] border border-[#2E3240] rounded-xl p-4 flex items-center gap-4 col-span-2 lg:col-span-1">
            <div className="relative w-16 h-16 flex-shrink-0">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 96 96">
                <circle cx="48" cy="48" r="40" stroke="#2E3240" strokeWidth="5" fill="none" />
                <circle cx="48" cy="48" r="40" stroke={safetyScoreColor} strokeWidth="5" fill="none"
                  strokeDasharray={`${scoreDash} 251.3`} strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.8s' }} />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-lg font-black" style={{ color: safetyScoreColor }}>{summary.overallScore}</span>
            </div>
            <div>
              <p className="text-[10px] text-gray-600 uppercase">Safety Score</p>
              <p className="text-xs font-bold" style={{ color: safetyScoreColor }}>{safetyScoreLabel}</p>
            </div>
          </div>
          {[
            { label: 'Total Content', value: summary.total, icon: FileText, color: '#fff' },
            { label: 'Safe', value: summary.safe, icon: CheckCircle, color: '#4ade80' },
            { label: 'Warnings', value: summary.warning, icon: AlertTriangle, color: '#FFB020' },
            { label: 'Flagged', value: summary.flagged, icon: XCircle, color: '#EF4444' },
          ].map(s => (
            <div key={s.label} className="bg-[#242730] border border-[#2E3240] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <s.icon className="h-3.5 w-3.5" style={{ color: s.color }} />
                <p className="text-[9px] text-gray-600 uppercase tracking-wider">{s.label}</p>
              </div>
              <p className="text-2xl font-black" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* FTC Compliance + Mention Tracker row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-[#242730] border border-[#2E3240] rounded-xl p-5">
            <h3 className="text-xs font-bold text-white mb-3 flex items-center gap-2"><Hash className="h-3.5 w-3.5 text-[#00D4FF]" />FTC Compliance Rate</h3>
            <div className="flex items-center gap-4">
              <p className="text-3xl font-black text-[#00D4FF]">{summary.complianceRate}%</p>
              <div className="flex-1">
                <div className="w-full h-2 bg-[#181A20] rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-[#00D4FF]" style={{ width: `${summary.complianceRate}%`, transition: 'width 0.5s' }} />
                </div>
                <p className="text-[9px] text-gray-600 mt-1">{contentItems.filter(c => c.compliance_score === 100).length} of {summary.total} posts include #ad</p>
              </div>
            </div>
          </div>

          <div className="bg-[#242730] border border-[#2E3240] rounded-xl p-5">
            <h3 className="text-xs font-bold text-white mb-3 flex items-center gap-2"><Activity className="h-3.5 w-3.5 text-[#FF4D94]" />Sentiment Breakdown</h3>
            <div className="flex gap-4">
              {[
                { label: 'Positive', count: contentItems.filter(c => c.sentiment === 'positive').length, color: '#4ade80' },
                { label: 'Neutral', count: contentItems.filter(c => c.sentiment === 'neutral').length, color: '#94A3B8' },
                { label: 'Negative', count: contentItems.filter(c => c.sentiment === 'negative').length, color: '#FF4D94' },
              ].map(s => (
                <div key={s.label} className="text-center flex-1">
                  <p className="text-xl font-black" style={{ color: s.color }}>{s.count}</p>
                  <p className="text-[9px] text-gray-600">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#242730] border border-[#2E3240] rounded-xl p-5">
            <h3 className="text-xs font-bold text-white mb-3 flex items-center gap-2"><TrendingUp className="h-3.5 w-3.5 text-[#00D4FF]" />Brand Mentions</h3>
            <div className="space-y-2">
              {[
                { platform: 'TikTok', mentions: '4.2K', sentiment: 'positive' },
                { platform: 'Instagram', mentions: '3.8K', sentiment: 'positive' },
                { platform: 'YouTube', mentions: '1.1K', sentiment: 'neutral' },
              ].map(p => (
                <div key={p.platform} className="flex items-center justify-between text-[11px]">
                  <span className="text-gray-500">{p.platform}</span>
                  <span className="font-bold text-white">{p.mentions}</span>
                  <span className={p.sentiment === 'positive' ? 'text-green-400' : 'text-gray-400'}>{p.sentiment}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1 bg-[#242730] rounded-lg p-1">
            {['all', 'flagged', 'warning', 'safe', 'paused'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-[10px] font-semibold capitalize transition-all ${filter === f ? 'bg-[#00D4FF]/15 text-[#00D4FF]' : 'text-gray-500 hover:text-gray-300'}`}
                data-testid={`filter-${f}`}
              >{f === 'all' ? `All (${summary.total})` : `${f} (${summary[f] || 0})`}</button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-600" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search content or creators..."
              className="w-full pl-9 pr-3 py-2 bg-[#242730] border border-[#2E3240] rounded-lg text-xs text-white placeholder-gray-600 focus:border-[#00D4FF] focus:outline-none"
              data-testid="safety-search" />
          </div>
        </div>

        {/* Content Feed */}
        <div className="space-y-2" data-testid="safety-content-feed">
          {filtered.map(item => {
            const statusStyle = STATUS_COLORS[item.safety_status] || STATUS_COLORS.safe;
            const StatusIcon = statusStyle.icon;
            const sentimentInfo = SENTIMENT_ICONS[item.sentiment] || SENTIMENT_ICONS.neutral;
            const SentimentIcon = sentimentInfo.icon;
            return (
              <div key={item.id} className={`bg-[#242730] border ${STATUS_BORDER[item.safety_status] || 'border-[#2E3240]'} rounded-xl p-4 hover:border-[#00D4FF]/20 transition-all`}
                data-testid={`content-item-${item.id}`}>
                <div className="flex items-start gap-4">
                  {/* Status indicator */}
                  <div className={`w-9 h-9 rounded-lg ${statusStyle.bg} flex items-center justify-center flex-shrink-0`}>
                    <StatusIcon className={`h-4 w-4 ${statusStyle.text}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-white">{item.creator_name}</span>
                      <span className="text-[10px] text-[#00D4FF]">{item.creator_handle}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#181A20] text-gray-500 border border-[#2E3240]">{item.platform}</span>
                      <Badge className={`text-[8px] ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>{item.safety_status}</Badge>
                      {item.wave && <span className="text-[8px] text-gray-600">{item.wave}</span>}
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed mb-2 line-clamp-2">{item.caption}</p>

                    {/* Issues */}
                    {item.issues && item.issues.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {item.issues.map((issue, idx) => (
                          <span key={`${issue.type}-${idx}`} className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${issue.severity === 'high' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}>
                            {issue.message || issue.type}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Metrics row */}
                    <div className="flex items-center gap-4 text-[10px] text-gray-500">
                      <span className="flex items-center gap-1"><SentimentIcon className={`h-3 w-3 ${sentimentInfo.color}`} />{item.sentiment}</span>
                      {item.engagement && <span>Engagement: <span className="text-white font-semibold">{item.engagement}</span></span>}
                      {item.reach && <span>Reach: <span className="text-white font-semibold">{item.reach}</span></span>}
                      <span>Compliance: <span className={`font-semibold ${item.compliance_score === 100 ? 'text-green-400' : 'text-yellow-400'}`}>{item.compliance_score}%</span></span>
                      {item.posted && <span className="text-gray-600">{item.posted}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1.5 flex-shrink-0">
                    {item.safety_status === 'flagged' || item.safety_status === 'warning' ? (
                      <>
                        <button onClick={() => handleAction(item.id, 'approve')}
                          className="p-2 rounded-lg bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20 transition-all"
                          data-testid={`approve-${item.id}`} title="Approve & Notify">
                          <CheckCircle className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => handleAction(item.id, 'revision')}
                          className="p-2 rounded-lg bg-[#FFB020]/10 text-[#FFB020] border border-[#FFB020]/20 hover:bg-[#FFB020]/20 transition-all"
                          data-testid={`revision-${item.id}`} title="Send Revision Request">
                          <RefreshCw className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => handleAction(item.id, 'pause')}
                          className="p-2 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-all"
                          data-testid={`pause-${item.id}`} title="Auto-Pause">
                          <Pause className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : item.safety_status === 'paused' ? (
                      <button onClick={() => handleAction(item.id, 'approve')}
                        className="p-2 rounded-lg bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/20 hover:bg-[#00D4FF]/20 transition-all"
                        data-testid={`resume-${item.id}`} title="Resume">
                        <Play className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-500 text-sm">No content matches your filters.</div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
